import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * `--paper` on a 1px `--light` border, 5px radius, and NO SHADOW.
 *
 * The no-shadow rule is doing work. UI-UX-SPEC §2 allows exactly one shadow in the product — on a
 * dragged or focused element — so that when something lifts, it means it is moving. A page of
 * softly floating cards spends that signal on nothing.
 */
export function Card({
  className,
  lifted = false,
  children,
}: {
  className?: string;
  /** The one sanctioned shadow: this element is being dragged, or holds focus. */
  lifted?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={clsx(
        'rounded-card border border-light bg-paper',
        lifted && 'shadow-lifted',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A label above a heading — small, uppercase, tracked out. `--ink-3`, which the spec designates for
 * labels and explicitly NOT for running text: it is held to 3:1 rather than 4.5:1, which is right
 * for four tracked-out words and wrong for a sentence.
 */
/**
 * ⚠ `ink-3` IS ONLY LEGIBLE ON PAPER AND CHALK.
 *
 * It measures 5.12 and 4.58 there, and FAILS on every tinted ground — 4.15 on light, 4.29 on
 * tint-deep, 4.18 on tint-signal, 3.78 on sand. An 11px line gets no large-text allowance whatever
 * it is called, so a card with a tinted background must pass `className="text-ink-2"`, which clears
 * 4.5 on all of them.
 *
 * The default stays `ink-3` because most cards are paper. `contrast.test.ts` pins every one of those
 * numbers, and the accessibility sweep catches the ones that slip through.
 */
export function Eyebrow({
  ground = 'paper',
  className,
  children,
}: {
  /**
   * WHAT IS BEHIND IT, because that is what decides whether it is readable.
   *
   * Defaulted rather than required, unlike `SectionHead` on the public site — almost every card in
   * the signed-in app is paper, and making four callers say so to protect one would be noise. The
   * one that is not says so.
   */
  ground?: 'paper' | 'tinted';
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      className={clsx(
        'text-[11px] font-semibold uppercase tracking-[0.14em]',
        /*
          THE COLOUR IS RESOLVED HERE, NOT PASSED IN, and that is the whole point.

          It used to be a hardcoded `text-ink-3` that a caller could "override" with
          `className="text-ink-2"` — except `clsx` concatenates, it does not merge, so the element
          got BOTH classes and Tailwind resolved them by stylesheet order. `ink-3` won, the override
          did nothing, and the page looked exactly as if the fix had worked.

          That is the same failure this project has written down before: a dead class looks precisely
          like a design choice.
        */
        ground === 'tinted' ? 'text-ink-2' : 'text-ink-3',
        className,
      )}
    >
      {children}
    </p>
  );
}
